<template>
  <aw-drag
    :enable="bcDocumentStore.edit"
    :drag-item="element"
    #default="{ dragHandle }"
    class="DocumentBlock -color"
    :class="{
      isCommentOpened: isCommentOpened,
      DocumentHighlightSearch: $route.hash === '#el' + element.id
    }"
    :id="'el' + element.id"
  >
    <div class="DocumentElement -color">
      <div
        class="w-full bg-bc-blue flex flex-row justify-center space-x-2 p-4"
        v-if="element.locked && !isEveryElementLocked && !isPageLocked"
      >
        <aw-icon icon="lock" class="w-5 h-5 text-white " />
        <span class="text-white text-sm">{{ $t('Element is locked') }}</span>
      </div>

      <div class="ColorRowSeparator | bg-bc-gray-bg h-px -ml-5 -mr-5 md:ml-0 md:mr-0"></div>

      <div class="flex w-full gap-x-4 md:gap-x-8 mt-4 md:mt-8">
        <!-- Desktop square -->
        <div
          class="w-32 h-32 lg:w-44 lg:h-44 ml-8 mb-4 | hidden md:block"
          :class="{ 'mr-8': edit }"
          :style="{ background: getBackground }"
        ></div>

        <!-- Mobile stripe -->
        <div
          class="ColorRowMobileStripe w-12 lg:w-40  md:hidden"
          :class="{ 'mr-8': edit }"
          :style="{ background: getBackground }"
        ></div>

        <div class="w-full md:w-1/2">
          <div class="text-xl font-semibold mb-2.5">
            <contenteditable
              tag="span"
              v-model="data.name"
              :noNL="true"
              :contenteditable="edit"
              :noHTML="true"
              :alwaysSelectAll="$t('Enter color name')"
              @updated="updated = 'name'"
              :auto-focus="!element.id"
            />
          </div>

          <aw-drop
            :list="children"
            :can-drop-item="canDropItem"
            :create-placeholder="createPlaceholder"
            transition-name="document-elements"
            list-item-key="key"
            @drop-item="drop"
            class="ColorList text-sm"
          >
            <template #header>
              <div class="ColorList__Row">
                <div class="relative">RGB</div>
                <aw-copy-to-clipboard 
                  :text="r + '/' + g + '/' + b"
                  v-slot="{copy, status}"
                >
                  <div v-if="edit" class="ColorList__Row__Value flex space-x-1">
                    <contenteditable
                      tag="span"
                      v-model="r"
                      :noNL="true"
                      :contenteditable="edit"
                      :noHTML="true"
                      :alwaysSelectAll="true"
                      @updated="updated = 'r'"
                      class="px-2"
                    />
                    <span>/</span>
                    <contenteditable
                      tag="span"
                      v-model="g"
                      :noNL="true"
                      :contenteditable="edit"
                      :noHTML="true"
                      :alwaysSelectAll="true"
                      @updated="updated = 'g'"
                      class="px-2"
                    />
                    <span>/</span>
                    <contenteditable
                      tag="span"
                      v-model="b"
                      :noNL="true"
                      :contenteditable="edit"
                      :noHTML="true"
                      :alwaysSelectAll="true"
                      @updated="updated = 'b'"
                      class="px-2"
                    />
                  </div>
                  <div v-else class="ColorList__Row__Value px-2">
                    {{ r }} / {{ g }} / {{ b }}
                  </div>
                  <div class="AwClipboard__Icon w-3 h-full">
                    <aw-icon 
                      v-show="status !== 'copied'"
                      icon="clipboard-copy" 
                      class="w-3 h-3 -mt-1 text-gray-400 hover:text-bc-blue cursor-pointer"
                      @click="copy()"
                    />
                  </div>
                </aw-copy-to-clipboard>
              </div>
              <div class="ColorList__Row">
                <div class="relative">HEX</div>
                <aw-copy-to-clipboard 
                  :text="hex"
                  v-slot="{copy, status}"
                >
                  <div v-if="edit" class="ColorList__Row__Value flex space-x-1">
                    <contenteditable
                      tag="span"
                      v-model="hex"
                      :noNL="true"
                      :contenteditable="edit"
                      :noHTML="true"
                      :alwaysSelectAll="true"
                      class="px-2"
                    />
                  </div>
                  <div v-else class="ColorList__Row__Value px-2 uppercase">
                    {{ hex }}
                  </div>
                  <div class="AwClipboard__Icon w-3 h-full">
                    <aw-icon 
                      v-show="status !== 'copied'"
                      icon="clipboard-copy" 
                      class="w-3 h-3 -mt-1 text-gray-400 hover:text-bc-blue cursor-pointer"
                      @click="copy()"
                    />
                  </div>
                </aw-copy-to-clipboard>
              </div>
            </template>
            <template #default="{ item, index }">
              <!-- U radku se inverze prohazuji barevnosti -->

              <!-- Realny radek -->
              <document-element-color-row
                :key="item.key"
                :element="item"
                :parent="element"
                :page="page"
                :index="index"
                :class="{
                  DocumentHasPlaceholder: item.placeholder,
                  DocumentElementDragged: item.dragged
                }"
              />
            </template>

            <template #footer>
              <document-add-color-row
                :parent="element"
                :page="page"
                :index="children.length"
                v-if="edit"
              />
            </template>
          </aw-drop>
        </div>
      </div>

      <!-- comments -->
      <document-comment
        v-if="bcDocumentStore.document.allow_comments"
        v-model="element.comments"
        :element="element"
        :document="bcDocumentStore.document"
        @comment-toggle="resolveZindexOnParent"
      />

      <!-- panel s moznosti noveho elementu -->
      <document-hot-action
        :parent="parent"
        :page="page"
        :index="index + 1"
        v-if="edit && !isLastOnPage"
      />
    </div>
    <div class="DocumentBlock__PanelRight -color">
      <span
        ref="DocumentCircle"
        class="DocumentCircle mr-2.5 sm:mr-3.5"
        :class="{
          'DocumentCircle--active': bcSelectedStore.selected.has(element.id)
        }"
        :data-item-index="element.root_rank"
        @click.prevent.stop="bcSelectedStore.select(
          element, 
          {
            isRecursive: true,
            isShift: $event.shiftKey,
            isDocument: true,
            itemIndex: element.root_rank,
          }
        )"
      ></span>
      <span
        class="cursor-move text-gray-400 hover:text-gray-500"
        v-if="edit"
        :ref="dragHandle"
      >
        <aw-icon icon="dots" class="w-4 h-4 -mx-1 "></aw-icon>
        <aw-icon icon="dots" class="w-4 h-4 -mx-1 "></aw-icon>
      </span>
    </div>
    <div class="DocumentBlock__PanelLeft -color">
      <!-- {* flex kvuli ikonam v dropdownu a zarovnani *} -->
      <!-- <document-add-color-row
        :parent="element"
        :page="page"
        :index="children.length"
        v-if="edit"
      /> -->
    </div>
  </aw-drag>
</template>

<script>
import BaseElement from '../../mixins/BaseElement'
import Selected from '../../mixins/Selected'

export default {
  mixins: [
    BaseElement,
    Selected,
  ],
  allowedChildElements: ['color-row'],
  updateDataKeys: ['text_color'],
  data() {
    return {
      hexValue: 'fff',
    }
  },
  computed: {
    getBackground() {
      return this.rgbToHex(this.data.r, this.data.g, this.data.b)
    },
    textColorStyle() {
      return {
        color: this.data.text_color
      }
    },
    hex: {
      get() {
        return this.rgbToHex(this.data.r, this.data.g, this.data.b)
      },
      set(val) {
        const parsedHex = this.parseHex(val)

        if (!parsedHex) {
          return
        }

        this.data.r = parsedHex.r
        this.data.g = parsedHex.g
        this.data.b = parsedHex.b
        this.updated = true
      }
    },
    r: {
      get() {
        return this.data.r.toString()
      },
      set(val) {
        //this.data.r = val
        //this.$nextTick(() => {
        this.data.r = this.validateColor(val)
        //})
      }
    },
    g: {
      get() {
        return this.data.g.toString()
      },
      set(val) {
        //this.data.g = val
        //this.$nextTick(() => {
        this.data.g = this.validateColor(val)
        //})
      }
    },
    b: {
      get() {
        return this.data.b.toString()
      },
      set(val) {
        //this.data.b = val
        //this.$nextTick(() => {
        this.data.b = this.validateColor(val)
        //})
      }
    }
  },
  methods: {
    cleanHex(hex) {
      // Check if hex is invalid (null, undefined, or not 6 characters)
      if (!hex || hex.length < 6) return null

      // Clean the hex string (convert to uppercase and remove # if present)
      let _cleaned = hex.toUpperCase()
      if (_cleaned.startsWith('#')) _cleaned = _cleaned.substr(1)

      // Return null if length is not exactly 6
      if (_cleaned.length !== 6) return null

      // Validate if all characters are valid hex chars
      if (!/^[0-9A-F]+$/.test(_cleaned)) return null

      let r, g, b
      r = g = b = null

      // Extract RGB values from the 6-character hex string
      r = _cleaned[0] + _cleaned[1]
      g = _cleaned[2] + _cleaned[3]
      b = _cleaned[4] + _cleaned[5]

      return `#${r}${g}${b}`
    },
    parseHex(hex) {
      const cleanedHex = this.cleanHex(hex)
      if (!cleanedHex) {
        return null
      }

      let hexArr = cleanedHex.substr(1).split('')

      let h = hexArr[0] + hexArr[1]
      let e = hexArr[2] + hexArr[3]
      let x = hexArr[4] + hexArr[5]
      let r = parseInt(h, 16)
      let g = parseInt(e, 16)
      let b = parseInt(x, 16)

      return {
        r,
        g,
        b,
      }
    },
    validateColor(num) {
      num = parseInt(num) || 0
      if (num > 255) {
        num = 255
      } else if (num < 0) {
        num = 0
      }

      return num
    },
    componentToHex(c) {
      const hex = parseInt(c).toString(16)
      return (hex.length == 1 ? '0' + hex : hex).toUpperCase()
    },
    rgbToHex(r, g, b) {
      return (
        '#' +
        this.componentToHex(r) +
        this.componentToHex(g) +
        this.componentToHex(b)
      )
    }
  },
  mounted() {
    this.assignItemToSelectBtn('DocumentCircle', this.element)
  },
}
</script>

<style lang="scss" scoped>
.Mover {
  @apply hidden md:block;
}

.text-non-white {
  color: var(--primaryColor);
}

.ColorList__Row .AwClipboard__Icon svg {
  visibility: hidden;
  opacity: 0;
  transition:  opacity 150ms ease-in-out;
}

.ColorList__Row:has(.ColorList__Row__Value:hover) .AwClipboard__Icon svg,
.ColorList__Row:has(.AwClipboard__Icon:hover) .AwClipboard__Icon svg {
  visibility: visible;
  opacity: 1;
}
</style>
